import React, { useState } from "react";
import { useSnackbar } from "notistack";
import { 
  Box, 
  Card, 
  Button, 
  Typography, 
  Container,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Alert
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQueryClient } from "react-query";

import PersonalDetails from "./PersonalDetails";
import ContactDetails from "./ContactDetails";
import ReferenceInformation from "./ReferenceInformation";
import GeneralInfo from "./GeneralInfo";

import { ClientService } from "../../api/services";
import { QK } from "../../base/qk";

const defaultAddress = { 
  id: 1, 
  address_1: "", 
  address_2: "", 
  town: "", 
  county: "", 
  postcode: "",
  lat: null,
  lng: null,
  address_type: "permanent",
  is_service_delivery: true 
};

const defaultPhone = { 
  id: 1, 
  number: "", 
  phone_type: "mobile", 
  preference: 1 
};

const defaultEmail = { 
  id: 1, 
  email: "", 
  email_type: "personal", 
  preference: 1 
};

const initialFormData = {
  first_name: "",
  middle_name: "",
  last_name: "",
  title: "",
  suffix_name: "",
  maiden_name: "",
  preferred_name: "",
  birthdate: "",
  marital_status: "",
  gender: "",
  religion: "",
  ethnicity: "",
  nhs_number: "",
  status: "Active",
  location: "",
  area: "",
  preferred_contact_method: "",
  addresses: [defaultAddress],
  phones: [defaultPhone],
  emails: [defaultEmail],
  ref_service_user_id: "",
  ref_location: "",
  ref_start_date: "",
  ref_end_date: "",
  ref_date_of_death: "",
  ref_key_worker: "",
  ref_named_nurse: "",
  ni_number: "",
  area_preference: "",
  order_number: "",
  as_number: "",
  las_number: "",
  case_number: "",
  sproc_number: "",
  ref_funding_route: "",
  ref_category_of_care: "",
};

const ClientManagement = ({ setActiveKey, setClientId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target || {};
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const setFormDataValue = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const createClientMutation = useMutation(
    (data) => ClientService.createClient(data),
    {
      onSuccess: (response) => {
        enqueueSnackbar("Client has been created successfully.", { variant: "success" });
        queryClient.invalidateQueries(QK.clients);
        if (setActiveKey) setActiveKey("create-visit");
        if (setClientId) setClientId(response.data.id);
        setFormData(initialFormData);
        setErrors({});
      },
      onError: (error) => {
        console.error("Error:", error);
        let errorMessage = "Something went wrong. Please try again.";
        const errorObj = {};
        
        if (error.response?.data) {
          const data = error.response.data;
          if (typeof data === "object") {
            Object.entries(data).forEach(([field, messages]) => {
              const msg = Array.isArray(messages) ? messages[0] : messages;
              errorObj[field] = msg;
              errorMessage = msg;
              enqueueSnackbar(`${field}: ${msg}`, { variant: "error" });
            });
            setErrors(errorObj);
          } else if (typeof data === "string") {
            enqueueSnackbar(data, { variant: "error" });
          }
        } else if (error.message) {
          enqueueSnackbar(error.message, { variant: "error" });
        }
      }
    }
  );

  const buildPayload = () => ({
    first_name: formData.first_name,
    middle_name: formData.middle_name,
    last_name: formData.last_name,
    title: formData.title,
    suffix_name: formData.suffix_name,
    maiden_name: formData.maiden_name,
    preferred_name: formData.preferred_name,
    birthdate: formData.birthdate || null,
    marital_status: formData.marital_status,
    gender: formData.gender,
    religion: formData.religion,
    ethnicity: formData.ethnicity,
    nhs_number: formData.nhs_number,
    status: formData.status,
    location: formData.location,
    area: formData.area,
    preferred_contact_method: formData.preferred_contact_method,
    addresses: formData.addresses,
    phones: formData.phones,
    emails: formData.emails,
    ref_service_user_id: formData.ref_service_user_id,
    ref_location: formData.ref_location,
    ref_start_date: formData.ref_start_date || null,
    ref_end_date: formData.ref_end_date || null,
    ref_date_of_death: formData.ref_date_of_death || null,
    ref_key_worker: formData.ref_key_worker,
    ref_named_nurse: formData.ref_named_nurse,
    ni_number: formData.ni_number,
    area_preference: formData.area_preference,
    order_number: formData.order_number,
    as_number: formData.as_number,
    las_number: formData.las_number,
    case_number: formData.case_number,
    sproc_number: formData.sproc_number,
    ref_funding_route: formData.ref_funding_route,
    ref_category_of_care: formData.ref_category_of_care,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    createClientMutation.mutate(payload);
  };

  return (
    <Container maxWidth="xl" sx={{ pt: 2, pb:4 }}>
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#101828' }}>
              Create a Service User
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fill in the information below to register a new Service User.
            </Typography>
          </Box>
          <Button 
            variant="outlined"
            component={Link} 
            to="/clients" 
            sx={{ 
              borderRadius: '8px', 
              textTransform: "none", 
              borderColor: '#D0D5DD',
              color: '#344054',
              '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D0D5DD' }
            }}
          >
            Back to Dashboard
          </Button>
        </Stack>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Please fix the following errors:</Typography>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field}>
                    <strong>{field.replace(/_/g, ' ').toUpperCase()}:</strong> {message}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <GeneralInfo 
            handleChange={handleChange} 
            data={formData} 
            setFormData={setFormDataValue}
            errors={errors}
          />
          <PersonalDetails 
            handleChange={handleChange} 
            data={formData}
            setFormData={setFormDataValue}
            errors={errors}
          />
          <ContactDetails 
            handleChange={handleChange} 
            data={formData}
            setFormData={setFormDataValue}
            errors={errors}
          />
          <ReferenceInformation 
            handleChange={handleChange} 
            data={formData}
            setFormData={setFormDataValue}
            errors={errors}
          />

          <Card 
            variant="outlined" 
            sx={{ 
              p: 2, 
              borderRadius: '12px', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 2,
              boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.02)',
              border: '1px solid #EAECF0'
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/clients')}
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px', 
                px: 3, 
                textTransform: 'none',
                color: '#344054',
                borderColor: '#D0D5DD'
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              startIcon={<SaveIcon />}
              disabled={createClientMutation.isLoading}
              sx={{ 
                borderRadius: '8px', 
                px: 4, 
                textTransform: 'none',
                bgcolor: '#1570EF',
                '&:hover': { bgcolor: '#1056B3' }
              }}
            >
              {createClientMutation.isLoading ? "Saving..." : "Save Client"}
            </Button>
          </Card>
        </Stack>
      </Box>
    </Container>
  );
};

export default ClientManagement;